import { Injectable } from '@angular/core';
import { ISignatureFieldMeta, SignatureField } from '../../../../interfaces/signature';

export interface IFieldRelationship {
  parentType: string;
  childrenTypes: string[];
}

// NOTE: the type values in the fields must match the server values
export enum FieldTypes {
  SIGNATURE = 'signature',
  NAME = 'name',
  DATE_SIGNED = 'date-signed',
  TITLE = 'title',
  INITIAL_HERE = 'initial-here',
  APPROVE = 'approve',
  SIGNER_ATTACHMENT = 'signer-attachment',
  TEXT = 'text',
  DATE = 'date',
  URL = 'url',
  CHECKBOX = 'checkbox',
  RADIO_GROUP = 'radio-group',
  RADIO_BUTTON = 'radio-button',
}

// NOTE: the type values in the fields must match the server values
export enum PropertiesKeys {
  RECIPIENT_NO = 'recipientNo',
  REQUIRED = 'required',
  TAB_LABEL = 'tabLabel',
  GROUP_NAME = 'groupName',
  DATE_VALIDATION_TYPE = 'dateValidationType',
  TOOL_TIP = 'toolTip',
  URL = 'url',
  URL_DISPLAY_TEXT = 'urlDisplayText',
  TAB_VALUE = 'tabValue',
}

@Injectable({ providedIn: 'root' })
export class DocusignOptionsService {
  private static UK_DATE_FORMAT = {
    label: 'dd/MM/yyyy',
    validationType: 'UK_DATE_VALIDATION',
  };
  private static US_DATE_FORMAT = {
    label: 'MM/dd/yyyy',
    validationType: 'US_DATE_VALIDATION',
  };
  private static ISO_DATE_FORMAT = {
    label: 'yyyy/MM/dd',
    validationType: 'ISO_DATE_VALIDATION',
  };

  public static dateValidationPatterns: any[] = [
    DocusignOptionsService.UK_DATE_FORMAT,
    DocusignOptionsService.US_DATE_FORMAT,
    DocusignOptionsService.ISO_DATE_FORMAT,
  ];

  public static REQUIRED_DEFAULT = true;
  public static DATE_VALIDATION_DEFAULT = DocusignOptionsService.UK_DATE_FORMAT.validationType;
  public static DEFAULT_TOOL_TIP = undefined;
  public static DEFAULT_URL = undefined;
  public static DEFAULT_URL_DISPLAY_TEXT = undefined;
  public static DEFAULT_TAB_VALUE = undefined;

  // NOTE: the type values in the fields must match the server values
  public static fields: ISignatureFieldMeta[] = [
    { type: FieldTypes.SIGNATURE, label: 'Signature', isParent: false },
    { type: FieldTypes.NAME, label: 'Name', isParent: false },
    { type: FieldTypes.DATE_SIGNED, label: 'Date signed', isParent: false },
    { type: FieldTypes.TITLE, label: 'Title', isParent: false },
    { type: FieldTypes.INITIAL_HERE, label: 'Initial', isParent: false },
    { type: FieldTypes.APPROVE, label: 'Approve', isParent: false },
    {
      type: FieldTypes.SIGNER_ATTACHMENT,
      label: 'Attachment',
      isParent: false,
    },
    { type: FieldTypes.TEXT, label: 'Text', isParent: false },
    { type: FieldTypes.DATE, label: 'Date', isParent: false },
    { type: FieldTypes.URL, label: 'URL', isParent: false },
    { type: FieldTypes.CHECKBOX, label: 'Checkbox', isParent: false },
    { type: FieldTypes.RADIO_GROUP, label: 'Radio group', isParent: true },
  ];

  public static childFields: ISignatureFieldMeta[] = [{ type: FieldTypes.RADIO_BUTTON, label: 'Radio button', isParent: false }];

  public static fieldRelationships: IFieldRelationship[] = [
    {
      parentType: FieldTypes.RADIO_GROUP,
      childrenTypes: [FieldTypes.RADIO_BUTTON],
    },
  ];

  public static addDefaultValues(field: any) {
    DocusignOptionsService.updateRequired(field);
    DocusignOptionsService.updateDateValidation(field);
    DocusignOptionsService.updateToolTip(field);
    DocusignOptionsService.updateUrlEntry(field);
    DocusignOptionsService.updateTabValue(field);
  }

  public static updateRequired(field: any, isRequired?: boolean) {
    if (DocusignOptionsService.supportsRequired(field.type)) {
      field.properties[PropertiesKeys.REQUIRED] = isRequired !== undefined ? isRequired : DocusignOptionsService.REQUIRED_DEFAULT;
    }
  }

  public static updateDateValidation(field: any, value?: any) {
    if (DocusignOptionsService.supportsDateValidation(field.type)) {
      field.properties[PropertiesKeys.DATE_VALIDATION_TYPE] = value || DocusignOptionsService.DATE_VALIDATION_DEFAULT;
    }
  }

  public static updateToolTip(field: any, toolTipValue?: any) {
    if (DocusignOptionsService.supportsToolTip(field.type)) {
      field.properties[PropertiesKeys.TOOL_TIP] = toolTipValue || DocusignOptionsService.DEFAULT_TOOL_TIP;
    }
  }

  public static updateUrlEntry(field: any, urlValue?: any, urlDisplayText?: any) {
    if (DocusignOptionsService.supportsUrlEntry(field.type)) {
      field.properties[PropertiesKeys.URL] = urlValue || DocusignOptionsService.DEFAULT_URL;
      field.properties[PropertiesKeys.URL_DISPLAY_TEXT] = urlDisplayText || DocusignOptionsService.DEFAULT_URL_DISPLAY_TEXT;
    }
  }

  public static updateTabValue(field: any, tabValue?: any) {
    if (DocusignOptionsService.supportsTabValue(field.type)) {
      field.properties[PropertiesKeys.TAB_VALUE] = tabValue || DocusignOptionsService.DEFAULT_TAB_VALUE;
    }
  }

  // fields with values which can be written back to Bullhorn
  public static supportsDataLabel(fieldType: string): boolean {
    return (
      fieldType === FieldTypes.NAME ||
      fieldType === FieldTypes.TITLE ||
      fieldType === FieldTypes.DATE_SIGNED ||
      fieldType === FieldTypes.TEXT ||
      fieldType === FieldTypes.DATE ||
      fieldType === FieldTypes.RADIO_GROUP
    );
  }

  public static hasOptions(fieldType: string): boolean {
    return (
      this.supportsRequired(fieldType) ||
      this.supportsDateValidation(fieldType) ||
      this.supportsToolTip(fieldType) ||
      this.supportsUrlEntry(fieldType) ||
      this.supportsRadioButtons(fieldType) ||
      this.supportsTabValue(fieldType)
    );
  }

  public static supportsRequired(fieldType: string): boolean {
    return (
      fieldType === FieldTypes.TITLE ||
      fieldType === FieldTypes.SIGNATURE ||
      fieldType === FieldTypes.INITIAL_HERE ||
      fieldType === FieldTypes.SIGNER_ATTACHMENT ||
      fieldType === FieldTypes.TEXT ||
      fieldType === FieldTypes.DATE ||
      fieldType === FieldTypes.RADIO_GROUP
    );
  }

  public static supportsDateValidation(fieldType: string): boolean {
    return fieldType === FieldTypes.DATE;
  }

  public static supportsToolTip(fieldType: string): boolean {
    return (
      fieldType === FieldTypes.TEXT ||
      fieldType === FieldTypes.DATE ||
      fieldType === FieldTypes.RADIO_GROUP ||
      fieldType === FieldTypes.CHECKBOX
    );
  }

  public static supportsUrlEntry(fieldType: string): boolean {
    return fieldType === FieldTypes.URL;
  }

  public static supportsRadioButtons(fieldType: string): boolean {
    return fieldType === FieldTypes.RADIO_GROUP;
  }

  public static supportsTabValue(fieldType: string): boolean {
    return fieldType === FieldTypes.RADIO_BUTTON;
  }

  public static legacyFieldWithNoRequiredOption(field: SignatureField): boolean {
    return this.supportsRequired(field.type) && !field.properties.hasOwnProperty('required');
  }

  public static hasChildFields(fieldType: string): boolean {
    const field = DocusignOptionsService.fields.find((type) => fieldType === type.type);
    return field?.isParent || false;
  }

  public static getChildren(fieldType: string): ISignatureFieldMeta[] {
    const relationship = DocusignOptionsService.fieldRelationships.find((value) => value.parentType === fieldType);
    return relationship
      ? DocusignOptionsService.childFields.filter((childField) => relationship.childrenTypes.includes(childField.type))
      : [];
  }
}
